import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '../../lib/api';
import { localInputToISO, isoToLocalInput, formatConferenceDateTime } from '../../lib/timezone';
import { Session, Room } from '../../types/session';
import { Button, Card, Input, Textarea, Select } from '../../components/ui';

interface SessionSubmitData {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  roomId: string;
  speakerName: string;
  speakerBio?: string;
  speakerAffiliation?: string;
  materials?: string[];
  track?: string;
  prerequisites?: string;
}

interface SessionFormData {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  roomId: string;
  speakerName: string;
  speakerBio: string;
  speakerAffiliation: string;
  materials: string;
  track: string;
  prerequisites: string;
}

interface SessionWithRoom extends Omit<Session, 'room'> {
  roomId: string;
  room: Room;
}

export default function SessionManager() {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<SessionWithRoom | null>(null);
  const [formData, setFormData] = useState<SessionFormData>({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    roomId: '',
    speakerName: '',
    speakerBio: '',
    speakerAffiliation: '',
    materials: '',
    track: '',
    prerequisites: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof SessionFormData, string>>>({});
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Fetch sessions
  const { data: sessions, isLoading: sessionsLoading } = useQuery<SessionWithRoom[]>({
    queryKey: ['admin', 'sessions'],
    queryFn: () => fetchApi<SessionWithRoom[]>('/admin/sessions'),
  });

  // Fetch rooms for dropdown
  const { data: rooms } = useQuery<Room[]>({
    queryKey: ['rooms'],
    queryFn: () => fetchApi<Room[]>('/rooms'),
  });

  // Create session mutation
  const createMutation = useMutation({
    mutationFn: (data: SessionSubmitData) =>
      fetchApi('/admin/sessions', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'sessions'] });
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      showNotification('success', 'Session created successfully');
      resetForm();
    },
    onError: (error: Error) => {
      showNotification('error', `Failed to create session: ${error.message}`);
    },
  });

  // Update session mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: SessionSubmitData }) =>
      fetchApi(`/admin/sessions/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'sessions'] });
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      showNotification('success', 'Session updated successfully');
      resetForm();
    },
    onError: (error: Error) => {
      showNotification('error', `Failed to update session: ${error.message}`);
    },
  });

  // Delete session mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      fetchApi(`/admin/sessions/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'sessions'] });
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      showNotification('success', 'Session deleted successfully');
    },
    onError: (error: Error) => {
      showNotification('error', `Failed to delete session: ${error.message}`);
    },
  });

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof SessionFormData, string>> = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    if (!formData.startTime) {
      errors.startTime = 'Start time is required';
    }
    if (!formData.endTime) {
      errors.endTime = 'End time is required';
    }
    if (!formData.roomId) {
      errors.roomId = 'Room is required';
    }
    if (!formData.speakerName.trim()) {
      errors.speakerName = 'Speaker name is required';
    }

    // Validate start time is before end time
    if (formData.startTime && formData.endTime) {
      const start = new Date(formData.startTime);
      const end = new Date(formData.endTime);
      if (start >= end) {
        errors.endTime = 'End time must be after start time';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Parse materials from comma-separated string to array
    const materials = formData.materials
      .split(',')
      .map((m) => m.trim())
      .filter((m) => m.length > 0);

    const submitData: SessionSubmitData = {
      title: formData.title,
      description: formData.description,
      startTime: localInputToISO(formData.startTime),
      endTime: localInputToISO(formData.endTime),
      roomId: formData.roomId,
      speakerName: formData.speakerName,
      speakerBio: formData.speakerBio || undefined,
      speakerAffiliation: formData.speakerAffiliation || undefined,
      materials: materials.length > 0 ? materials : undefined,
      track: formData.track || undefined,
      prerequisites: formData.prerequisites || undefined,
    };

    if (editingSession) {
      updateMutation.mutate({ id: editingSession.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (session: SessionWithRoom) => {
    setEditingSession(session);
    setFormData({
      title: session.title,
      description: session.description,
      startTime: isoToLocalInput(session.startTime),
      endTime: isoToLocalInput(session.endTime),
      roomId: session.roomId,
      speakerName: session.speakerName,
      speakerBio: session.speakerBio || '',
      speakerAffiliation: session.speakerAffiliation || '',
      materials: session.materials?.join(', ') || '',
      track: session.track || '',
      prerequisites: session.prerequisites || '',
    });
    setFormErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = (id: string, title: string) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      startTime: '',
      endTime: '',
      roomId: '',
      speakerName: '',
      speakerBio: '',
      speakerAffiliation: '',
      materials: '',
      track: '',
      prerequisites: '',
    });
    setFormErrors({});
    setEditingSession(null);
    setIsFormOpen(false);
  };

  const formatDateTime = (dateString: string) => formatConferenceDateTime(dateString);

  if (sessionsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-usc-garnet mx-auto"></div>
          <p className="mt-4 text-usc-gray-600">Loading sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-usc-garnet">Session Management</h2>
          <p className="text-usc-gray-600 mt-2">Create and manage conference sessions</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          + Create Session
        </Button>
      </div>

      {/* Notification Toast */}
      {notification && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            notification.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {notification.message}
        </div>
      )}

      {/* Session Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-2xl font-bold text-usc-garnet mb-6">
                {editingSession ? 'Edit Session' : 'Create New Session'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Title *"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  error={formErrors.title}
                  placeholder="Session title"
                />

                <Textarea
                  label="Description *"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  error={formErrors.description}
                  placeholder="Session description"
                  rows={4}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Start Time *"
                    type="datetime-local"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    error={formErrors.startTime}
                  />

                  <Input
                    label="End Time *"
                    type="datetime-local"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    error={formErrors.endTime}
                  />
                </div>

                <Select
                  label="Room *"
                  value={formData.roomId}
                  onChange={(e) => setFormData({ ...formData, roomId: e.target.value })}
                  error={formErrors.roomId}
                >
                  <option value="">Select a room</option>
                  {rooms?.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name} - {room.building} (Capacity: {room.capacity})
                    </option>
                  ))}
                </Select>

                <Input
                  label="Speaker Name *"
                  value={formData.speakerName}
                  onChange={(e) => setFormData({ ...formData, speakerName: e.target.value })}
                  error={formErrors.speakerName}
                  placeholder="Speaker name"
                />

                <Textarea
                  label="Speaker Bio"
                  value={formData.speakerBio}
                  onChange={(e) => setFormData({ ...formData, speakerBio: e.target.value })}
                  placeholder="Speaker biography"
                  rows={3}
                />

                <Input
                  label="Speaker Affiliation"
                  value={formData.speakerAffiliation}
                  onChange={(e) => setFormData({ ...formData, speakerAffiliation: e.target.value })}
                  placeholder="Organization or institution"
                />

                <Input
                  label="Track"
                  value={formData.track}
                  onChange={(e) => setFormData({ ...formData, track: e.target.value })}
                  placeholder="e.g., AI/ML, Data Science, Cloud Computing"
                />

                <Textarea
                  label="Prerequisites"
                  value={formData.prerequisites}
                  onChange={(e) => setFormData({ ...formData, prerequisites: e.target.value })}
                  placeholder="Required knowledge or skills"
                  rows={2}
                />

                <Input
                  label="Materials"
                  value={formData.materials}
                  onChange={(e) => setFormData({ ...formData, materials: e.target.value })}
                  placeholder="Comma-separated URLs (e.g., https://example.com/slides.pdf, https://...)"
                  helperText="Enter URLs separated by commas"
                />

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    {createMutation.isPending || updateMutation.isPending
                      ? 'Saving...'
                      : editingSession
                      ? 'Update Session'
                      : 'Create Session'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {/* Sessions List */}
      <div className="space-y-4">
        {sessions && sessions.length > 0 ? (
          sessions.map((session) => (
            <Card key={session.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-usc-garnet mb-2">{session.title}</h3>
                  <p className="text-usc-gray-700 mb-3">{session.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-usc-gray-600">Time:</span>{' '}
                      <span className="text-usc-gray-900">
                        {formatDateTime(session.startTime)} - {formatDateTime(session.endTime)}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-usc-gray-600">Room:</span>{' '}
                      <span className="text-usc-gray-900">
                        {session.room.name} ({session.room.building})
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-usc-gray-600">Speaker:</span>{' '}
                      <span className="text-usc-gray-900">{session.speakerName}</span>
                    </div>
                    {session.track && (
                      <div>
                        <span className="font-medium text-usc-gray-600">Track:</span>{' '}
                        <span className="text-usc-gray-900">{session.track}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex space-x-2 ml-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(session)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => handleDelete(session.id, session.title)}
                    disabled={deleteMutation.isPending}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <Card className="p-12 text-center">
            <p className="text-usc-gray-600 text-lg">No sessions found</p>
            <p className="text-usc-gray-500 mt-2">Create your first session to get started</p>
          </Card>
        )}
      </div>
    </div>
  );
}
